/** @ngInject */
import { Component, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import _ from 'lodash';
import { first, map } from 'rxjs/operators';
import { RouterStateService } from '../../../core/routing/router-state.service';
import { LaunchDarklyService } from '../../../core/services/launchdarkly.service';
import { PendoFeedbackService } from '../../../core/services/pendo-feedback.service';
import { FeatureService } from '../../../core/services/permission/feature.service';
import { PermissionService } from '../../../core/services/permission/permission.service';
import { StoresService } from '../../../core/services/stores';
import { UserService } from '../../../core/services/user';
import { IntegrationProvider } from '../../../data/models/integrations.model';
import { RepoJobsService } from '../../../data/repositories/repo-jobs.service';
import { RepoSettingsService } from '../../../data/repositories/repo-settings.service';
import { SharedDataService } from '../../../data/services/shared-data.service';
import { mapToArray } from '../../../utils/helper-functions/array.functions';
import { AccountIntegrationsStoresService } from './../../account/integrations/feat-account-integrations/domain/apis/account-integrations.stores';
import { Sidebar2SubpanelSharedService } from './sidebar2-subpanel-shared.service';
import { Sidebar2SubpanelStateService } from './sidebar2-subpanel-state.service';

@UntilDestroy()
@Component({
    selector: 'app-sidebar2-subpanel',
    templateUrl: './sidebar2-subpanel.component.html',
    standalone: false,
})
export class Sidebar2SubpanelComponent implements OnInit {
    // @ts-ignore TS7008
    config;
    users = [];

    hasMultipleTenants = false;

    // Toggles
    toggle_MyProfile = true;
    toggle_SwitchBusiness = false;
    toggle_Connections = true;

    radioBusinessOption = '';
    showSwitchBusiness = false;
    pendoGuides = [];

    country = { label: 'Australia', value: 'AU' };
    countries = [
        { label: 'Australia', value: 'AU' },
        { label: 'United Kingdom', value: 'GB' },
        { label: 'Canada', value: 'CA' },
    ];

    // @ts-ignore TS7008
    accountOptions;
    // @ts-ignore TS7008
    invoiceCounts;
    // @ts-ignore TS7008
    orderCounts;
    // @ts-ignore TS7008
    estimateStatuses;
    // @ts-ignore TS7008
    jobStatuses;
    // @ts-ignore TS7008
    customerStatuses;
    // @ts-ignore TS7008
    contactStatuses;
    // @ts-ignore TS7008
    quoteRequestStatuses;
    // @ts-ignore TS7008
    filterState;
    // @ts-ignore TS7008
    showIntegrationsSubpanel;
    // @ts-ignore TS7008
    integrationsActive;

    // @ts-ignore TS7008
    showMyBusinessSubpanel;
    // @ts-ignore TS7008
    showLeadsSubpanel;
    // @ts-ignore TS7008
    showEstimatesSubpanel;
    // @ts-ignore TS7008
    showJobsSubpanel;
    // @ts-ignore TS7008
    showRequestForQuotesSubpanel;
    // @ts-ignore TS7008
    showClientsSubpanel;
    // @ts-ignore TS7008
    showContactsSubpanel;
    // @ts-ignore TS7008
    showCataloguesSubpanel;
    // @ts-ignore TS7008
    showCommunicationsSubpanel;

    // @ts-ignore TS7008
    connectionStatus;
    // @ts-ignore TS7008
    preSelected;
    // @ts-ignore TS7008
    communicationsOptions;
    // @ts-ignore TS7008
    type;

    showAppVersion = window.APP_CONFIG.environment !== 'Production';
    appVersion = window.APP_CONFIG.appVersion;
    routerUrl: string = '';

    showTenantHierarchy = false;
    showAPIWebhooks = false;
    exploreMoreWithResources = signal(false);
    // Not supporting MYOB yet
    canViewImportedBills$ = this.accountIntegrationsStoresService.getAccountingIntegration$.pipe(
        map((integration) => integration?.provider === IntegrationProvider.Xero || integration?.provider === IntegrationProvider.QuickBooks)
    );

    constructor(
        public featureService: FeatureService,
        public permissionService: PermissionService,
        private sidebar2SubpanelStateService: Sidebar2SubpanelStateService,
        private userService: UserService,
        private accountIntegrationsStoresService: AccountIntegrationsStoresService,
        private storesService: StoresService,
        private jobsRepo: RepoJobsService,
        public settings: RepoSettingsService,
        private sidebar2SharedService: Sidebar2SubpanelSharedService,
        private pendoFeedbackService: PendoFeedbackService,
        private lds: LaunchDarklyService,
        public sharedData: SharedDataService,
        private routerStateService: RouterStateService,
        private router: Router
    ) {}

    ngOnInit() {
        this.setupPanels();

        this.routerStateService.routeChanged$.pipe(untilDestroyed(this)).subscribe(() => {
            this.routerUrl = this.routerStateService.getCurrentUrlRoute();
            this.setupPanels();
            this.getSubPanelData(); // this is calling the GetRecipieLists
        });

        this.storesService.setupSubpanels$.pipe(untilDestroyed(this)).subscribe(() => {
            this.setupPanels();
        });

        /* Receiving from Sidebar v2 component for Communications */

        this.storesService.updateNotificationsCount$.pipe(untilDestroyed(this)).subscribe(() => {
            this.setNotificationBadges();
        });

        this.getTenants();

        this.accountOptions = this.sidebar2SharedService.getAccountOptions();

        this.invoiceCounts = this.resetInvoiceCounts();

        this.orderCounts = this.resetOrderCounts();

        this.getSubPanelData();

        this.storesService.getSubpanelEstimatesUpdate$.pipe(untilDestroyed(this)).subscribe((e) => {
            if (e) this.estimateStatuses = this.sidebar2SubpanelStateService.getEstimates();
        });

        this.storesService.getSubpanelJobsUpdate$.pipe(untilDestroyed(this)).subscribe((e) => {
            if (e) this.jobStatuses = this.sidebar2SubpanelStateService.getJobs();
        });

        this.storesService.getSubpanelClientsUpdate$.pipe(untilDestroyed(this)).subscribe((e) => {
            if (e) this.customerStatuses = this.sidebar2SubpanelStateService.getCustomers();
        });

        this.storesService.getSubpanelContactsUpdate$.pipe(untilDestroyed(this)).subscribe((e) => {
            if (e) this.contactStatuses = this.sidebar2SubpanelStateService.getContacts();
        });

        this.storesService.getSubpanelQuoteRequestsUpdate$.pipe(untilDestroyed(this)).subscribe((e) => {
            if (e) this.quoteRequestStatuses = this.sidebar2SubpanelStateService.getQuoteRequests();
        });

        this.accountIntegrationsStoresService.isSyncIntegrationCompleted$.pipe(untilDestroyed(this)).subscribe(() => {
            this.determineVisibilityOfConnections();
        });

        // Recycle Bin - disable filters on delete view

        this.storesService.updateSubpanelFilterStatus$.pipe(untilDestroyed(this)).subscribe((opt) => {
            this.filterState = opt;
        });

        this.showTenantHierarchy = this.lds.get('group-tenants') ?? false;
        this.showAPIWebhooks = this.permissionService.isAvailable('api.webhooks-get');
        this.exploreMoreWithResources.set(this.lds.get('explore-more-with-resources'));
    }

    /* AKA Panels that are toggle panels, without routes */
    RouteLessPanels() {
        return this.settings.ShowExplorePanel || this.settings.ShowMyProfilePanel;
    }

    determineVisibilityOfConnections() {
        if (this.showIntegrationsSubpanel) {
            this.integrationsActive = this.sidebar2SharedService.getVisibilityOfConnections();
        }
    }

    setupPanels() {
        const myBusinessRoutes = ['/account/details', '/account/users', '/account/subscription', '/account/setup/', '/account/api/'];
        const myBusinessExcludedRoutes = ['/account/setup/integrations'];

        // Set up booleans
        this.showMyBusinessSubpanel =
            !this.RouteLessPanels() &&
            myBusinessRoutes.some((accountRoute) => this.routerUrl.startsWith(accountRoute)) &&
            !myBusinessExcludedRoutes.some((accountRoute) => this.routerUrl.startsWith(accountRoute)) &&
            this.permissionService.isAvailable('account.maintain-account-details.account-details-menu') &&
            this.featureService.isStateAvailable('account.details');

        this.showLeadsSubpanel = !this.RouteLessPanels() && this.routerUrl.startsWith('/leads');

        this.showEstimatesSubpanel =
            !this.RouteLessPanels() &&
            this.routerUrl.startsWith('/estimates') &&
            this.permissionService.isAvailable('estimates.estimates-access.estimates-menu') &&
            this.featureService.isStateAvailable('estimates');

        this.showJobsSubpanel =
            !this.RouteLessPanels() &&
            this.routerUrl.startsWith('/jobs') &&
            this.permissionService.isAvailable('jobs.jobs-access.jobs-menu') &&
            this.featureService.isStateAvailable('jobs');

        this.showRequestForQuotesSubpanel =
            !this.RouteLessPanels() &&
            this.routerUrl.startsWith('/quoterequests') &&
            this.permissionService.isAvailable('request-for-quote.rfq-access.rfq-menu') &&
            this.featureService.isStateAvailable('quoterequests');

        this.showClientsSubpanel =
            !this.RouteLessPanels() &&
            this.routerUrl.startsWith('/customers') &&
            this.permissionService.isAvailable('customers.customers-access.customers-menu') &&
            this.featureService.isStateAvailable('customers');

        this.showContactsSubpanel =
            !this.RouteLessPanels() &&
            this.routerUrl.startsWith('/contacts') &&
            this.permissionService.isAvailable('contacts.contacts-access.contacts-menu') &&
            this.featureService.isStateAvailable('contacts');

        this.showCataloguesSubpanel =
            !this.RouteLessPanels() &&
            this.routerUrl.startsWith('/price') &&
            this.permissionService.isAvailable('pricing.pricing-access.pricing-menu') &&
            this.featureService.isStateAvailable('pricing');

        this.showIntegrationsSubpanel =
            !this.RouteLessPanels() &&
            (this.routerUrl.startsWith('/account/setup/integrations') || this.routerUrl.startsWith('/integrations')) &&
            this.permissionService.isAvailable('account.maintain-integrations.integrations-menu') &&
            this.featureService.isStateAvailable('account.integrations') &&
            (this.featureService.isStateAvailable('account.integrations.applications') ||
                this.featureService.isStateAvailable('account.integrations.addons') ||
                (this.featureService.isStateAvailable('account.integrations.suppliers') &&
                    !this.featureService.isAvailable('manufacturer')));

        this.showCommunicationsSubpanel =
            !this.RouteLessPanels() &&
            this.routerUrl.startsWith('/messages') &&
            this.permissionService.isAvailable('messages.messages-access.messages-menu') &&
            this.featureService.isStateAvailable('messages');

        this.determineVisibilityOfConnections();
    }

    // @ts-ignore TS7006
    getDropdownClasses(submenu, fixedChevron?) {
        let classStrings = [];

        if (this.routerStateService.currentRoute?.queryParams?.submenu === submenu) {
            classStrings = [];
            classStrings.push('active');
            if (this.filterState && this.filterState.submenu === submenu) {
                if (!this.filterState.enabled) classStrings.push('disabled');
            }
            if (this.routerStateService.currentRoute?.queryParams?.open === 'true' && !fixedChevron) classStrings.push('open');
        }

        return classStrings.join(' ');
    }

    getSubPanelData() {
        if (this.showEstimatesSubpanel) {
            this.estimateStatuses = this.sidebar2SubpanelStateService.getEstimates();
        } else if (this.showJobsSubpanel) {
            this.jobStatuses = this.sidebar2SubpanelStateService.getJobs();
            this.updateJobInvoiceCounts(null); // If we don't have specific job yet
            this.updateJobOrderCounts(null);
        } else if (this.showContactsSubpanel) {
            this.contactStatuses = this.sidebar2SubpanelStateService.getContacts();
        } else if (this.showClientsSubpanel) {
            this.customerStatuses = this.sidebar2SubpanelStateService.getCustomers();
            if (this.featureService.isAvailable('merchant') || this.featureService.isAvailable('manufacturer')) {
                // subscribe to any count change with Approving and Decline status change
                this.sidebar2SubpanelStateService.$supplierConnectionsCount.pipe(untilDestroyed(this)).subscribe((val) => {
                    this.connectionStatus = val;
                });

                this.sidebar2SubpanelStateService.getCustomerConnectionStatusCount();
            }
        } else if (this.showRequestForQuotesSubpanel) {
            this.quoteRequestStatuses = this.sidebar2SubpanelStateService.getQuoteRequests();
        } else if (this.showCommunicationsSubpanel) {
            this.setNotificationBadges();
        }
    }

    // @ts-ignore TS7006
    preSelect(selected, $event) {
        this.preSelected = selected;
    }

    fnToggleMyProfile() {
        this.toggle_MyProfile = !this.toggle_MyProfile;
    }

    fnToggleIntegrationsConnections() {
        this.toggle_Connections = !this.toggle_Connections;
    }

    fnToggleSwitchBusiness() {
        this.toggle_SwitchBusiness = !this.toggle_SwitchBusiness;
    }

    fnSignOut() {
        this.sidebar2SharedService.signOut();
    }

    // @ts-ignore TS7006
    fnSwitchTenant(e) {
        e.preventDefault();
        this.router.navigate(['/tenant-selection']);
    }

    getTenants() {
        this.userService.avaliableTenants$.pipe(first()).subscribe((tenants) => {
            this.hasMultipleTenants = tenants.length > 1;
        });
    }

    setNotificationBadges() {
        this.communicationsOptions = this.sidebar2SharedService.getNotificationBadges();
    }

    resetInvoiceCounts() {
        return this.sidebar2SharedService.resetInvoiceCounts();
    }

    resetOrderCounts() {
        return this.sidebar2SharedService.resetOrderCounts();
    }

    subscribe() {
        this.router.navigate(['/account', 'subscription']);
    }

    // @ts-ignore TS7006
    updateJobInvoiceCounts(job) {
        this.invoiceCounts = this.resetInvoiceCounts();
        if (job) {
            _.forEach(job.JobPayments, (p) => {
                if (!p.IsDeleted && !p.IsDepositInvoice) {
                    this.invoiceCounts.all.count++;
                    this.invoiceCounts.pending.count += p.Status === 'Pending' ? 1 : 0;
                    this.invoiceCounts.invoiced.count += p.Status === 'Invoiced' ? 1 : 0;
                    this.invoiceCounts.partreceived.count += p.Status === 'PartReceived' ? 1 : 0;
                    this.invoiceCounts.received.count += p.Status === 'Received' ? 1 : 0;
                }
                if (!p.IsDeleted && p.IsDepositInvoice) this.invoiceCounts.deposit.count += p.IsDepositInvoice ? 1 : 0;
            });
        }
    }

    // @ts-ignore TS7006
    mapToArray(map) {
        return mapToArray(map);
    }

    // @ts-ignore TS7006
    updateJobOrderCounts(job) {
        this.orderCounts = this.resetOrderCounts();
        this.type = this.jobsRepo.GetPurchaseOrderType(this.routerStateService.currentRoute?.queryParams?.type);

        if (job) {
            _.forEach(job.PurchaseOrders, (p) => {
                if (!p.IsDeleted && p.OrderType === this.type.value) {
                    this.orderCounts.all.count++;
                    this.orderCounts.unsent.count += p.OrderStatus === 'Unsent' ? 1 : 0;
                    this.orderCounts.sent.count += p.OrderStatus === 'Sent' ? 1 : 0;
                    this.orderCounts.partreceived.count += p.OrderStatus === 'PartReceived' ? 1 : 0;
                    this.orderCounts.received.count += p.OrderStatus === 'Received' ? 1 : 0;
                    this.orderCounts.cancelled.count += p.OrderStatus === 'Cancelled' ? 1 : 0;
                    this.orderCounts.completed.count += p.OrderStatus === 'Completed' ? 1 : 0;
                }
            });
        }
    }

    /* BX-240 */
    loadPendoGuides() {
        this.pendoGuides = [];
        if (window.pendo && window.pendo.guides && window.pendo.guides.length) {
            // @ts-ignore TS7006
            window.pendo.guides.forEach((g) => {
                if (g.name !== 'Launcher' && g.isTopLevel) {
                    // @ts-ignore TS2322
                    this.pendoGuides.push({ title: g.name, modules: g.modules, dropdownOpen: false });
                }
            });
        }
    }

    // @ts-ignore TS7006
    toggleFeatureSuggestions(elem) {
        this.pendoFeedbackService.open(elem);
    }
}
